import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import {
    PLANOS,
    CONDICAO_ESPECIAL_TARIFAS,
    CONDICAO_ESPECIAL_BANDEIRAS,
    classificarPublico,
    getFatorFidelidade,
    isOpcaoCondicaoEspecial,
    planosSelecionaveis,
    PlanoComercial,
} from '../lib/politicaComercial';

interface CondicaoEspecialFieldsProps {
    /** true = condição especial já escolhida (plano fora_da_politica). */
    ativo: boolean;
    /** Chamado depois do aviso, com os percentuais iniciais já nas opções fixas. */
    onAtivar: (descTarifa: number, descBandeira: number) => void;
    /** Volta para os planos oficiais — o pai decide qual plano reaplicar. */
    onCancelar: () => void;
    descTarifa: number;
    descBandeira: number;
    onChangeDescTarifa: (v: number) => void;
    onChangeDescBandeira: (v: number) => void;
    volumeKwh?: number;
    fidelidade?: string | null;
}

const fmtMult = (v: number) => `${v.toFixed(2).replace('.', ',')}x`;

/**
 * Condição fora da política do vendedor externo (set/2026). Fica escondida
 * atrás de um link: antes de liberar os percentuais, mostra quanto a comissão
 * cai (0,30x contra os planos oficiais). Os percentuais são degraus fixos,
 * todos acima do maior plano — sem campo livre, não dá para pedir aprovação
 * de um valor que já é plano oficial ou que fica abaixo da política.
 */
const CondicaoEspecialFields: React.FC<CondicaoEspecialFieldsProps> = ({
    ativo,
    onAtivar,
    onCancelar,
    descTarifa,
    descBandeira,
    onChangeDescTarifa,
    onChangeDescBandeira,
    volumeKwh,
    fidelidade,
}) => {
    const [avisoAberto, setAvisoAberto] = useState(false);

    const publico = classificarPublico(Number(volumeKwh) || 0);
    const fora = PLANOS.find(p => p.foraDaPolitica)!;
    const oficiais = planosSelecionaveis().filter(p => !p.foraDaPolitica);
    // Todos os multiplicadores do aviso saem na MESMA base — plano × fidelidade.
    // Comparar o final da condição especial com o fator seco dos planos faria a
    // diferença parecer menor do que é (e no 20/50 a fidelidade nem escalona).
    const multFinal = (plano: PlanoComercial) =>
        Math.round(plano.mult[publico] * getFatorFidelidade(fidelidade, plano.codigo) * 100) / 100;
    const melhor = oficiais.reduce((a, b) => (multFinal(b) > multFinal(a) ? b : a));
    const maiorDesconto = oficiais.reduce((a, b) => (b.descTarifa > a.descTarifa ? b : a));
    const multFora = multFinal(fora);

    // Negociação antiga com percentual manual fora dos degraus: mostra o valor
    // gravado como opção para o save não trocar a condição sem o vendedor ver.
    const valorLegado = ativo && !isOpcaoCondicaoEspecial(descTarifa, descBandeira);
    const tarifas = valorLegado && !CONDICAO_ESPECIAL_TARIFAS.includes(descTarifa)
        ? [...CONDICAO_ESPECIAL_TARIFAS, descTarifa].sort((a, b) => a - b)
        : CONDICAO_ESPECIAL_TARIFAS;
    const bandeiras = valorLegado && !CONDICAO_ESPECIAL_BANDEIRAS.includes(descBandeira)
        ? [...CONDICAO_ESPECIAL_BANDEIRAS, descBandeira].sort((a, b) => a - b)
        : CONDICAO_ESPECIAL_BANDEIRAS;

    /** Seletor com seta dos dois lados: anda pelos degraus da lista, sem campo
     *  livre. A seta some (desabilita) na ponta — o valor nunca sai da grade. */
    const seletor = (
        label: string,
        valor: number,
        opcoes: number[],
        onChange: (v: number) => void
    ) => {
        const i = opcoes.indexOf(valor);
        const anterior = i > 0 ? opcoes[i - 1] : null;
        const proximo = i >= 0 && i < opcoes.length - 1 ? opcoes[i + 1] : null;
        const seta = (destino: number | null, Icone: typeof ChevronLeft, aria: string) => (
            <button
                type="button"
                aria-label={aria}
                disabled={destino === null}
                onClick={() => destino !== null && onChange(destino)}
                className="w-9 h-9 shrink-0 rounded-lg border border-orange-200 dark:border-orange-900/50 bg-white dark:bg-slate-900 text-orange-600 dark:text-orange-400 flex items-center justify-center transition-colors hover:bg-orange-100 dark:hover:bg-orange-900/30 disabled:opacity-30 disabled:hover:bg-white dark:disabled:hover:bg-slate-900 disabled:cursor-not-allowed"
            >
                <Icone size={18} />
            </button>
        );
        return (
            <div>
                <p className="text-[11px] font-bold text-gray-700 dark:text-slate-300 uppercase mb-1.5">{label}</p>
                <div className="flex items-center gap-2">
                    {seta(anterior, ChevronLeft, `Diminuir ${label.toLowerCase()}`)}
                    <div
                        role="status"
                        aria-live="polite"
                        className="flex-1 min-w-[88px] text-center rounded-lg border border-orange-300 dark:border-orange-800 bg-orange-100/70 dark:bg-orange-900/30 px-3 py-1.5 text-lg font-mono font-bold text-orange-700 dark:text-orange-300"
                    >
                        {valor}%
                    </div>
                    {seta(proximo, ChevronRight, `Aumentar ${label.toLowerCase()}`)}
                </div>
            </div>
        );
    };

    if (ativo) {
        return (
            <div className="rounded-xl border border-orange-200 dark:border-orange-900/50 bg-orange-50 dark:bg-orange-900/10 p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-xs text-orange-700 dark:text-orange-400 font-semibold flex items-center gap-1.5">
                        <AlertTriangle size={14} className="shrink-0" />
                        Condição especial · comissão {fmtMult(multFora)} · exige aprovação do gerente
                    </p>
                    <button
                        type="button"
                        onClick={onCancelar}
                        className="text-[11px] font-semibold text-primary hover:underline shrink-0"
                    >
                        Voltar aos planos
                    </button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    {seletor('Desconto na tarifa', descTarifa, tarifas, onChangeDescTarifa)}
                    {seletor('Desconto na bandeira', descBandeira, bandeiras, onChangeDescBandeira)}
                </div>
                {valorLegado && (
                    <p className="text-[11px] text-orange-600 dark:text-orange-400">
                        O valor gravado ({descTarifa}% / {descBandeira}%) não está entre as opções atuais. Ele continua valendo até você trocar.
                    </p>
                )}
            </div>
        );
    }

    const popup = avisoAberto ? createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="condicao-especial-titulo"
            className="fixed inset-0 z-[100002] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-in"
        >
            <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden border-2 border-red-500">
                <div className="bg-red-600 px-6 py-5 flex items-center gap-3">
                    <AlertTriangle size={40} className="text-white shrink-0" />
                    <h3 id="condicao-especial-titulo" className="text-2xl font-bold text-white leading-tight">
                        Isso derruba a sua comissão
                    </h3>
                </div>
                <div className="px-6 py-6 space-y-4">
                    <p className="text-base text-gray-700 dark:text-slate-200">
                        Nesta negociação sua comissão cai de
                        {' '}<strong className="font-mono text-gray-900 dark:text-white">{fmtMult(multFinal(melhor))}</strong>{' '}
                        no plano {melhor.label} para
                    </p>
                    <p className="text-center">
                        <span className="text-5xl font-bold font-mono text-red-600 dark:text-red-500">{fmtMult(multFora)}</span>
                    </p>
                    <p className="text-base text-gray-700 dark:text-slate-200">
                        Até o {maiorDesconto.label}, o maior desconto da política, paga
                        {' '}<strong className="font-mono text-gray-900 dark:text-white">{fmtMult(multFinal(maiorDesconto))}</strong>.
                    </p>
                    <p className="text-sm font-semibold text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/50 rounded-xl px-4 py-3">
                        A venda só vale depois que o gerente aprovar a condição.
                    </p>
                </div>
                <div className="px-6 py-4 bg-gray-50 dark:bg-slate-800/60 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                    <button
                        type="button"
                        autoFocus
                        onClick={() => setAvisoAberto(false)}
                        className="px-5 py-2.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90 transition-colors"
                    >
                        Manter plano da política
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setAvisoAberto(false);
                            onAtivar(CONDICAO_ESPECIAL_TARIFAS[0], CONDICAO_ESPECIAL_BANDEIRAS[CONDICAO_ESPECIAL_BANDEIRAS.length - 1]);
                        }}
                        className="px-5 py-2.5 rounded-xl border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                        Seguir e perder comissão
                    </button>
                </div>
            </div>
        </div>,
        document.body
    ) : null;

    return (
        <>
        {popup}
        <button
            type="button"
            onClick={() => setAvisoAberto(true)}
            className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-400"
        >
            Cliente precisa de desconto acima da política?
            <ChevronRight size={12} />
        </button>
        </>
    );
};

export default CondicaoEspecialFields;
